import { useCallback, useMemo } from 'react';
import { createLogger } from '../utils/logger';

/**
 * 门店刷新函数注册表
 * 用于存储每个门店的刷新函数，以便外部可以直接调用
 */
const storeRefreshFunctions = new Map<number | string, () => void>();

/**
 * 门店刷新函数注册 Hook
 * 用于注册和获取门店的刷新函数
 */
export interface UseStoreRefreshRegistryResult {
  registerRefresh: (storeId: number | string, refreshFn: () => void) => void; // 注册刷新函数
  unregisterRefresh: (storeId: number | string) => void; // 注销刷新函数
  triggerRefresh: (storeId: number | string) => void; // 触发刷新
}

export function useStoreRefreshRegistry(): UseStoreRefreshRegistryResult {
  const logger = useMemo(() => createLogger('useStoreRefreshRegistry'), []);
  
  /**
   * 注册门店的刷新函数
   */
  const registerRefresh = useCallback((storeId: number | string, refreshFn: () => void) => {
    storeRefreshFunctions.set(storeId, refreshFn);
    logger.debug(`注册门店 ${storeId} 的刷新函数`);
  }, [logger]);

  /**
   * 注销门店的刷新函数
   */
  const unregisterRefresh = useCallback((storeId: number | string) => {
    storeRefreshFunctions.delete(storeId);
    logger.debug(`注销门店 ${storeId} 的刷新函数`);
  }, [logger]);

  /**
   * 触发门店刷新
   */
  const triggerRefresh = useCallback((storeId: number | string) => {
    const refreshFn = storeRefreshFunctions.get(storeId);
    if (refreshFn) {
      logger.debug(`触发门店 ${storeId} 的刷新函数`);
      refreshFn();
    } else {
      logger.warn(`门店 ${storeId} 的刷新函数未注册`);
    }
  }, [logger]);

  return {
    registerRefresh,
    unregisterRefresh,
    triggerRefresh,
  };
}
